import React, { useEffect, useRef } from 'react';
import { geoOrthographic, geoPath, geoGraticule10 } from 'd3-geo';
import { feature } from 'topojson-client';
import land110m from 'world-atlas/land-110m.json';

const land = feature(land110m, land110m.objects.land);
const graticule = geoGraticule10();

// A self-contained stylized earth: blue ocean, green land, centered on the
// given coordinates with a glowing marker. No tiles / API keys. Optionally
// click-to-place (onPick) for the trip editor.
export default function Globe({ lat = 0, lng = 0, size = 280, dot = true, zoom = 1, onPick }) {
  const ref = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    cv.width = size * dpr;
    cv.height = size * dpr;
    const ctx = cv.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);

    const hasLoc = Number.isFinite(lat) && Number.isFinite(lng);
    // Clip the visible disc to the canvas so a zoomed (scaled-up) globe stays
    // inside the tile instead of overflowing.
    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - 1, 0, Math.PI * 2);
    ctx.clip();

    const projection = geoOrthographic()
      .scale((size / 2 - 2) * (zoom || 1))
      .translate([size / 2, size / 2])
      .clipAngle(90)
      .rotate(hasLoc ? [-lng, -lat] : [0, -20]);
    const path = geoPath(projection, ctx);

    // Ocean sphere.
    ctx.beginPath();
    path({ type: 'Sphere' });
    ctx.fillStyle = '#0b4f73';
    ctx.fill();
    // Subtle inner ocean gradient for depth.
    const grad = ctx.createRadialGradient(size * 0.38, size * 0.36, size * 0.1, size / 2, size / 2, size / 2);
    grad.addColorStop(0, 'rgba(40,160,200,0.45)');
    grad.addColorStop(1, 'rgba(4,30,50,0.15)');
    ctx.beginPath();
    path({ type: 'Sphere' });
    ctx.fillStyle = grad;
    ctx.fill();

    // Graticule.
    ctx.beginPath();
    path(graticule);
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 0.5;
    ctx.stroke();

    // Land.
    ctx.beginPath();
    path(land);
    ctx.fillStyle = '#2f9e57';
    ctx.fill();
    ctx.strokeStyle = 'rgba(10,40,20,0.5)';
    ctx.lineWidth = 0.4;
    ctx.stroke();

    // Rim light.
    ctx.beginPath();
    path({ type: 'Sphere' });
    ctx.strokeStyle = 'rgba(120,220,255,0.5)';
    ctx.lineWidth = 1.2;
    ctx.stroke();

    // Marker dot at the centered location.
    if (dot && hasLoc) {
      const p = projection([lng, lat]);
      if (p) {
        const [x, y] = p;
        ctx.beginPath();
        ctx.arc(x, y, 7, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(60,170,255,0.35)';
        ctx.fill();
        ctx.beginPath();
        ctx.arc(x, y, 3.5, 0, Math.PI * 2);
        ctx.fillStyle = '#3aa0ff';
        ctx.fill();
        ctx.lineWidth = 1.2;
        ctx.strokeStyle = '#fff';
        ctx.stroke();
      }
    }
    ctx.restore();
  }, [lat, lng, size, dot, zoom]);

  const handleClick = (e) => {
    if (!onPick) return;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const projection = geoOrthographic()
      .scale(size / 2 - 2)
      .translate([size / 2, size / 2])
      .clipAngle(90)
      .rotate(Number.isFinite(lat) && Number.isFinite(lng) ? [-lng, -lat] : [0, -20]);
    const inv = projection.invert([x, y]);
    if (inv && Number.isFinite(inv[0]) && Number.isFinite(inv[1])) {
      onPick({ lng: Math.round(inv[0] * 1000) / 1000, lat: Math.round(inv[1] * 1000) / 1000 });
    }
  };

  return (
    <canvas
      ref={ref}
      onClick={onPick ? handleClick : undefined}
      style={{ width: size, height: size, display: 'block', cursor: onPick ? 'crosshair' : 'default' }}
    />
  );
}
